import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import {
  MAX_AMOUNT,
  validateAmountInput,
  quotesUsdFormatter,
} from "./formatters";
import { writeCollapsedSiblings } from "./siblingWrites";
import { useCustomTextEditor } from "./useCustomTextEditor";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";

import type {
  TypedAmountTypeOption,
  TypedAmountValue,
} from "@platform/formatting-utils";
import type { ICellEditorParams } from "ag-grid-community";
import type { JsonValue } from "../utils/popupCustomText";

/** Parse the draft type/amount strings into a persistable `TypedAmountValue`. */
function resolveTypedAmountValue(
  draftType: string,
  draftAmount: string,
): TypedAmountValue {
  const parsedNumber = draftAmount === "" ? null : Number(draftAmount);
  return {
    type: draftType || undefined,
    amount: Number.isFinite(parsedNumber) ? (parsedNumber as number) : null,
  };
}

/** Resolve the value to persist for an extra input, accounting for visibility. */
function resolveExtraValue(
  extra: TypedAmountExtraInput,
  draftType: string,
  draftExtras: Record<string, string>,
  hideDependents: boolean,
): number | null {
  const isVisible =
    !hideDependents &&
    (!extra.showForTypes || extra.showForTypes.includes(draftType));
  if (!isVisible) return null;
  const raw = draftExtras[extra.key] ?? "";
  if (raw === "") return null;
  const parsed = Number(raw);

  return Number.isFinite(parsed) ? parsed : null;
}

/** Build the `[benefitKey, value]` pairs for the type, amount, and extras. */
function buildSiblingEntries(config: {
  typeKey: string;
  type: string | null;
  amountKey: string;
  amountValue: number | null;
  extraInputs: TypedAmountExtraInput[] | undefined;
  draftType: string;
  draftExtras: Record<string, string>;
  hideDependents: boolean;
}): [string, unknown][] {
  return [
    [config.typeKey, config.type],
    [config.amountKey, config.amountValue],
    ...(config.extraInputs ?? []).map((extra): [string, unknown] => [
      extra.key,
      resolveExtraValue(
        extra,
        config.draftType,
        config.draftExtras,
        config.hideDependents,
      ),
    ]),
  ];
}

/** Build the `[benefitKey, value]` pairs for a secondary type/amount group. */
function buildSecondaryGroupEntries(
  secondaryGroup: TypedAmountSecondaryGroup,
  draftSecondaryType: string,
  draftSecondaryAmount: string,
  showSecondaryGroup: boolean,
  showSecondaryAmountInput: boolean,
): [string, unknown][] {
  return [
    [
      secondaryGroup.typeKey,
      showSecondaryGroup ? draftSecondaryType || null : null,
    ],
    [
      secondaryGroup.amountKey,
      showSecondaryAmountInput && draftSecondaryAmount !== ""
        ? Number(draftSecondaryAmount)
        : null,
    ],
  ];
}

/** Build the `[benefitKey, value]` pairs for the primary group plus, when present, the secondary group and text extra. */
function buildAllSiblingEntries(config: {
  typeKey: string;
  type: string | null;
  amountKey: string;
  amountValue: number | null;
  extraInputs: TypedAmountExtraInput[] | undefined;
  draftType: string;
  draftExtras: Record<string, string>;
  hideDependents: boolean;
  secondaryGroup: TypedAmountSecondaryGroup | undefined;
  draftSecondaryType: string;
  draftSecondaryAmount: string;
  showSecondaryGroup: boolean;
  showSecondaryAmountInput: boolean;
  textExtra: TypedAmountTextExtra | undefined;
  draftTextExtra: string;
  showTextExtra: boolean;
}): [string, unknown][] {
  const primaryEntries = buildSiblingEntries(config);
  const secondaryEntries = config.secondaryGroup
    ? buildSecondaryGroupEntries(
        config.secondaryGroup,
        config.draftSecondaryType,
        config.draftSecondaryAmount,
        config.showSecondaryGroup,
        config.showSecondaryAmountInput,
      )
    : [];
  const textExtraEntries: [string, unknown][] = config.textExtra
    ? [
        [
          config.textExtra.key,
          config.showTextExtra ? config.draftTextExtra || null : null,
        ],
      ]
    : [];
  return [...primaryEntries, ...secondaryEntries, ...textExtraEntries];
}

/**
 * Collect validation errors for the amount and each visible extra input. The
 * amount uses `amountMin` / `amountInteger`; extra inputs use a minimum of 0
 * and are not constrained to whole numbers.
 */
const computeFieldErrors = (
  showAmountInput: boolean,
  draftAmount: string,
  amountMin: number,
  amountMax: number | undefined,
  amountInteger: boolean,
  amountOptional: boolean,
  visibleExtras: TypedAmountExtraInput[],
  draftExtras: Record<string, string>,
): {
  amountError: string;
  extraErrors: Record<string, string>;
  hasError: boolean;
} => {
  const amountError = showAmountInput
    ? validateAmountInput(draftAmount, {
        min: amountMin,
        max: amountMax,
        integer: amountInteger,
        optional: amountOptional,
      })
    : "";

  const extraErrors: Record<string, string> = {};

  for (const extra of visibleExtras) {
    extraErrors[extra.key] = validateAmountInput(draftExtras[extra.key] ?? "", {
      min: extra.min ?? 0,
      max: extra.max,
      integer: false,
    });
  }

  const hasError =
    amountError !== "" || Object.values(extraErrors).some((e) => e !== "");

  return { amountError, extraErrors, hasError };
};

const computeSecondaryAmountError = (
  showSecondaryAmountInput: boolean,
  draftSecondaryAmount: string,
  secondaryGroup?: TypedAmountSecondaryGroup,
  amountMax?: number,
): string =>
  showSecondaryAmountInput
    ? validateAmountInput(draftSecondaryAmount, {
        min: secondaryGroup?.amountMin ?? 0,
        max: amountMax,
        integer: secondaryGroup?.amountInteger ?? false,
      })
    : "";

export interface TypedAmountExtraInput {
  /** Sibling row's benefitKey for this extra field. */
  key: string;
  /** Input label shown in the editor. */
  label: string;
  /**
   * Initial value resolved from collapsed siblings: a number when saved,
   * `null` when explicitly cleared, `undefined` when never set.
   */
  initialValue?: number | null;
  /** Type values for which this input is shown. Omit to always show. */
  showForTypes?: string[];
  /** Pre-filled only when initialValue is `undefined` (never set). */
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
}

export interface TypedAmountTextExtra {
  /** Sibling row's benefitKey for this text field. */
  key: string;
  /** Input label shown in the editor. */
  label: string;
  /** Initial text value resolved from collapsed siblings. */
  initialValue?: string | null;
  maxLength?: number;
}

export interface TypedAmountSecondaryGroup {
  /** Section heading shown above this group's inputs. */
  label: string;
  initialTypedAmountValue: TypedAmountValue;
  typeOptions: TypedAmountTypeOption[];
  amountLabel?: string;
  /** Sibling row's benefitKey holding the type enum. */
  typeKey: string;
  /** Sibling row's benefitKey holding the amount number. */
  amountKey: string;
  amountMin?: number;
  amountInteger?: boolean;
  hideForTypes?: string[];
}

export interface TypedAmountCellEditorParams {
  /** Initial `{type, amount}` value to seed the form. */
  initialTypedAmountValue: TypedAmountValue;
  typeOptions: TypedAmountTypeOption[];
  amountLabel?: string;
  /** Sibling row's benefitKey holding the type enum. */
  typeKey: string;
  /** Sibling row's benefitKey holding the amount number. */
  amountKey: string;
  amountMin?: number;
  amountInteger?: boolean;
  /** When true, an empty amount saves as null instead of failing validation. */
  amountOptional?: boolean;
  /** Additional sibling fields beyond type/amount (e.g. duration for hospice). */
  extraInputs?: TypedAmountExtraInput[];
  /**
   * A second, independent `{type, amount}` pair rendered in its own section
   * (e.g. Vision's Additional Discounts on Frames, merged into the same
   * popup as Standard Frames Allowance).
   */
  secondaryGroup?: TypedAmountSecondaryGroup;
  textExtra?: TypedAmountTextExtra;
  /** Optional analyst-authored text attached to this popup-edited field. */
  initialCustomText?: string | null;
  /** Called on save when this popup supports custom text. */
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

type Props = ICellEditorParams & TypedAmountCellEditorParams;

/**
 * AG Grid popup cell editor for a typed-amount benefit field
 * (`{type, amount}`). Registered via the `CellEditorOverrideProvider`
 * in btx; AG Grid wraps this in a popup because `popup: true` is
 * returned from the editor selector.
 *
 * On save we write the companion sibling rows in the current column
 * (the type row, the amount row, and any extra inputs such as hospice's
 * duration) directly via `node.setDataValue` and mirror them into
 * `__collapsedSiblings` for display. The edited cell itself is only a
 * surrogate display row, so we cancel the parent cell commit and let the
 * formatter re-read the sibling values.
 *
 * Persistence flows from those sibling rows through
 * `useProposalPlanBenefitsMutation`, which transforms the dirty cells
 * back to the API shape and PUTs them to BTX.
 */
export const TypedAmountCellEditor = forwardRef<unknown, Props>(
  // eslint-disable-next-line complexity -- AG Grid cell editor uses imperative refs/side effects and is not React Compiler compatible
  function TypedAmountCellEditor(props, ref) {
    const {
      initialTypedAmountValue,
      typeOptions,
      amountLabel = "Amount",
      typeKey,
      amountKey,
      amountMin = 0,
      amountInteger = false,
      amountOptional = false,
      extraInputs,
      secondaryGroup,
      textExtra,
      initialCustomText,
      onCustomTextChange,
      api,
      colDef,
      column,
      node,
      stopEditing,
    } = props;

    const [draftType, setDraftType] = useState<string>(
      initialTypedAmountValue?.type ?? "",
    );
    const [draftAmount, setDraftAmount] = useState<string>(
      initialTypedAmountValue?.amount === undefined ||
        initialTypedAmountValue?.amount === null
        ? ""
        : String(initialTypedAmountValue?.amount),
    );
    const customText = useCustomTextEditor(
      initialCustomText,
      onCustomTextChange,
      stopEditing,
    );
    const { editingCustomText } = customText;
    const [draftTextExtra, setDraftTextExtra] = useState<string>(
      textExtra?.initialValue ?? "",
    );
    const [textExtraError, setTextExtraError] = useState("");
    const [draftExtras, setDraftExtras] = useState<Record<string, string>>(() =>
      Object.fromEntries(
        (extraInputs ?? []).map((e) => {
          const seed =
            e.initialValue !== undefined ? e.initialValue : e.defaultValue;
          return [e.key, seed != null ? String(seed) : ""];
        }),
      ),
    );
    const [amountError, setAmountError] = useState("");
    const [extraErrors, setExtraErrors] = useState<Record<string, string>>({});

    const [draftSecondaryType, setDraftSecondaryType] = useState<string>(
      secondaryGroup?.initialTypedAmountValue?.type ?? "",
    );
    const [draftSecondaryAmount, setDraftSecondaryAmount] = useState<string>(
      secondaryGroup?.initialTypedAmountValue?.amount === undefined ||
        secondaryGroup?.initialTypedAmountValue?.amount === null
        ? ""
        : String(secondaryGroup?.initialTypedAmountValue?.amount),
    );
    const [secondaryAmountError, setSecondaryAmountError] = useState("");

    // Exposed for AG Grid completeness; the save path cancels the parent
    // display-cell commit and persists through sibling rows instead.
    const lastValueRef = useRef<unknown>(initialTypedAmountValue?.type ?? null);

    // Focus the amount input on mount when shown. Avoids `autoFocus`
    // (jsx-a11y/no-autofocus) while still giving keyboard users an
    // immediate edit target inside the popup, which is the expected UX
    // for a cell editor opened by a deliberate action.
    const amountInputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      amountInputRef.current?.focus();
      amountInputRef.current?.select();
    }, []);

    const containerRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      getValue: () => lastValueRef.current,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const selectedOption = typeOptions.find((o) => o.value === draftType);
    // Only show the amount once a concrete type is chosen — with no type
    // selected `selectedOption` is undefined and the input must stay hidden.
    const showAmountInput =
      selectedOption != null && !selectedOption.hideAmount;

    const hideDependents =
      selectedOption == null || (selectedOption.hideDependents ?? false);

    const visibleExtras = hideDependents
      ? []
      : (extraInputs?.filter(
          (e) => !e.showForTypes || e.showForTypes.includes(draftType),
        ) ?? []);

    const showSecondaryGroup =
      !hideDependents &&
      secondaryGroup != null &&
      (!secondaryGroup.hideForTypes ||
        !secondaryGroup.hideForTypes.includes(draftType));

    const secondarySelectedOption = secondaryGroup?.typeOptions.find(
      (o) => o.value === draftSecondaryType,
    );
    const showSecondaryAmountInput =
      showSecondaryGroup &&
      secondarySelectedOption != null &&
      !secondarySelectedOption.hideAmount;

    // `silent` updates bypass `setDataValue`. Only `typeKey` calls it, so all
    // `__collapsedSiblings` are saved in one PATCH. Otherwise, each sibling
    // would trigger its own PATCH with stale, partial data.
    const writeSibling = (
      benefitKey: string,
      value: unknown,
      options?: { silent?: boolean },
    ): void => {
      const colKey = column ?? colDef?.field;
      if (!colKey) return;
      const colId = typeof colKey === "string" ? colKey : colKey.getColId();
      api.forEachNode((n) => {
        if (n.data?.benefitKey === benefitKey) {
          if (options?.silent) {
            n.data[colId] = value;
          } else {
            n.setDataValue(colKey, value);
          }
        }
      });
    };

    const handleSaveCustomText = (): void => {
      lastValueRef.current = initialTypedAmountValue?.type ?? null;
      customText.saveCustomText(initialTypedAmountValue);
    };

    const validateAndSetErrors = (): {
      errors: ReturnType<typeof computeFieldErrors>;
      secondaryError: string;
      textError: string;
    } => {
      const errors = computeFieldErrors(
        showAmountInput,
        draftAmount,
        amountMin,
        selectedOption?.amountMax,
        amountInteger,
        amountOptional,
        visibleExtras,
        draftExtras,
      );
      const secondaryError = computeSecondaryAmountError(
        showSecondaryAmountInput,
        draftSecondaryAmount,
        secondaryGroup,
        secondarySelectedOption?.amountMax,
      );
      const textError =
        textExtra?.maxLength != null &&
        draftTextExtra.length > textExtra.maxLength
          ? `Must be ${textExtra.maxLength} characters or fewer`
          : "";

      if (errors.hasError || secondaryError !== "" || textError !== "") {
        setAmountError(errors.amountError);
        setExtraErrors(errors.extraErrors);
        setSecondaryAmountError(secondaryError);
        setTextExtraError(textError);
      }

      return { errors, secondaryError, textError };
    };

    const handleSaveBenefit = (next: TypedAmountValue): void => {
      lastValueRef.current = next.type ?? null;

      const columnField = colDef?.field;
      const amountValue = selectedOption?.hideAmount ? null : next.amount;
      if (initialCustomText) onCustomTextChange?.(null);

      const collapsedNode = node as
        | { data: Record<string, unknown> }
        | undefined;

      const entries = buildAllSiblingEntries({
        typeKey,
        type: next.type ?? null,
        amountKey,
        amountValue,
        extraInputs,
        draftType,
        draftExtras,
        hideDependents,
        secondaryGroup,
        draftSecondaryType,
        draftSecondaryAmount,
        showSecondaryGroup,
        showSecondaryAmountInput,
        textExtra,
        draftTextExtra,
        showTextExtra: showAmountInput,
      });

      if (columnField && collapsedNode?.data) {
        writeCollapsedSiblings(collapsedNode, columnField, entries);
      }

      stopEditing(true);

      setTimeout(() => {
        for (const [benefitKey, value] of entries) {
          writeSibling(benefitKey, value, { silent: benefitKey !== typeKey });
        }
        if (node) api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    };

    const handleSave = (): void => {
      if (editingCustomText) {
        handleSaveCustomText();
        return;
      }

      const next = resolveTypedAmountValue(draftType, draftAmount);
      const { errors, secondaryError, textError } = validateAndSetErrors();
      const hasAnyError =
        errors.hasError || secondaryError !== "" || textError !== "";

      if (hasAnyError) return;

      handleSaveBenefit(next);
    };

    const handleCancel = (): void => {
      lastValueRef.current = initialTypedAmountValue.type ?? null;
      stopEditing(true);
    };

    usePopupEditorKeyboard(containerRef, handleSave, handleCancel);

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: 440,
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">
            <strong>Edit Benefit Value</strong>
          </Typography>

          {customText.customTextSupported && (
            <CustomTextModeToggle
              editMode={customText.editMode}
              onChange={customText.setEditMode}
            />
          )}

          {editingCustomText && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", mt: -0.5 }}
            >
              {`Benefit value: ${formatTypedAmountValue(
                initialTypedAmountValue,
                typeOptions,
                amountLabel,
              )}`}
            </Typography>
          )}

          {!editingCustomText && (
            <TextField
              select
              size="small"
              label="Type"
              value={draftType}
              onChange={(e) => {
                const nextType = e.target.value;
                setDraftType(nextType);
                setAmountError("");
                setExtraErrors({});

                const nextOption = typeOptions.find(
                  (o) => o.value === nextType,
                );
                if (nextOption?.hideDependents) {
                  setDraftExtras((prev) =>
                    Object.fromEntries(
                      Object.keys(prev).map((key) => [key, ""]),
                    ),
                  );
                  setDraftSecondaryType("");
                  setDraftSecondaryAmount("");
                  setSecondaryAmountError("");
                }
              }}
              fullWidth
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!editingCustomText && showAmountInput && (
            <TextField
              size="small"
              type="number"
              label={amountLabel}
              value={draftAmount}
              onChange={(e) => {
                setDraftAmount(e.target.value);
                setAmountError("");
              }}
              onBlur={() =>
                setAmountError(
                  validateAmountInput(draftAmount, {
                    min: amountMin,
                    max: selectedOption?.amountMax,
                    integer: amountInteger,
                    optional: amountOptional,
                  }),
                )
              }
              error={amountError !== ""}
              helperText={amountError || undefined}
              slotProps={{
                htmlInput: {
                  min: amountMin,
                  max: selectedOption?.amountMax ?? MAX_AMOUNT,
                  step: amountInteger ? 1 : "any",
                  onWheel: (e) => {
                    e.currentTarget.blur();
                  },
                },
              }}
              fullWidth
              inputRef={amountInputRef}
            />
          )}

          {!editingCustomText &&
            visibleExtras.map((extra) => (
              <TextField
                key={extra.key}
                size="small"
                type="number"
                label={extra.label}
                value={draftExtras[extra.key] ?? ""}
                onChange={(e) => {
                  setDraftExtras((prev) => ({
                    ...prev,
                    [extra.key]: e.target.value,
                  }));
                  setExtraErrors((prev) => ({ ...prev, [extra.key]: "" }));
                }}
                onBlur={() =>
                  setExtraErrors((prev) => ({
                    ...prev,
                    [extra.key]: validateAmountInput(
                      draftExtras[extra.key] ?? "",
                      { min: extra.min ?? 0, max: extra.max, integer: false },
                    ),
                  }))
                }
                error={(extraErrors[extra.key] ?? "") !== ""}
                helperText={extraErrors[extra.key] || undefined}
                inputProps={{
                  min: extra.min ?? 0,
                  max: extra.max,
                  step: extra.step,
                }}
                fullWidth
              />
            ))}

          {!editingCustomText && textExtra && showAmountInput && (
            <TextField
              size="small"
              label={textExtra.label}
              value={draftTextExtra}
              onChange={(e) => {
                setDraftTextExtra(e.target.value);
                setTextExtraError("");
              }}
              onBlur={() =>
                setTextExtraError(
                  textExtra.maxLength != null &&
                    draftTextExtra.length > textExtra.maxLength
                    ? `Must be ${textExtra.maxLength} characters or fewer`
                    : "",
                )
              }
              error={textExtraError !== ""}
              helperText={textExtraError || undefined}
              inputProps={{ maxLength: textExtra.maxLength }}
              fullWidth
              multiline
              minRows={2}
            />
          )}

          {!editingCustomText && showSecondaryGroup && secondaryGroup && (
            <Typography variant="body-medium" sx={{ mt: 1 }}>
              <strong>{secondaryGroup.label}</strong>
            </Typography>
          )}

          {!editingCustomText && showSecondaryGroup && secondaryGroup && (
            <TextField
              select
              size="small"
              label="Type"
              value={draftSecondaryType}
              onChange={(e) => {
                setDraftSecondaryType(e.target.value);
                setSecondaryAmountError("");
              }}
              fullWidth
            >
              {secondaryGroup.typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!editingCustomText && secondaryGroup && showSecondaryAmountInput && (
            <TextField
              size="small"
              type="number"
              label={secondaryGroup.amountLabel ?? "Amount"}
              value={draftSecondaryAmount}
              onChange={(e) => {
                setDraftSecondaryAmount(e.target.value);
                setSecondaryAmountError("");
              }}
              onBlur={() =>
                setSecondaryAmountError(
                  validateAmountInput(draftSecondaryAmount, {
                    min: secondaryGroup.amountMin ?? 0,
                    max: secondarySelectedOption?.amountMax,
                    integer: secondaryGroup.amountInteger ?? false,
                  }),
                )
              }
              error={secondaryAmountError !== ""}
              helperText={secondaryAmountError || undefined}
              slotProps={{
                htmlInput: {
                  min: secondaryGroup.amountMin ?? 0,
                  max: secondarySelectedOption?.amountMax ?? MAX_AMOUNT,
                  step: secondaryGroup.amountInteger ? 1 : "any",
                  onWheel: (e) => {
                    e.currentTarget.blur();
                  },
                },
              }}
              fullWidth
            />
          )}

          {editingCustomText && (
            <CustomTextEditorFields
              content={customText.draftCustomText}
              onUpdate={customText.setDraftCustomText}
              onCharacterCount={customText.setDraftCharacterCount}
            />
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  },
);

const formatTypedAmountValue = (
  value: TypedAmountValue,
  typeOptions: TypedAmountTypeOption[],
  _amountLabel: string,
): string => {
  if (!value.type) return "\u2014";
  const option = typeOptions.find((o) => o.value === value.type);
  const label = option?.label ?? value.type;
  if (
    option?.hideAmount ||
    value.amount === null ||
    value.amount === undefined
  ) {
    return label;
  }
  return quotesUsdFormatter.format(value.amount);
};
